import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { featuredMembers } from '@/mocks/members';
import { useScrollAnimation, useStaggerAnimation } from '@/hooks/useScrollAnimation';

const LOCATIONS = ['全部城市', '上海', '北京', '香港', '新加坡', '悉尼', '纽约', '伦敦', '东京', '广州', '迪拜', '巴黎', '多伦多'];
const PROFESSIONS = ['全部职业', '金融 / 投资', '科技 / 工程', '医疗 / 科研', '法律', '设计 / 艺术', '教育 / 咨询', '创业 / 管理', '贸易 / 商务'];

const professionMatch = (job: string, filter: string): boolean => {
  if (filter === '全部职业') return true;
  const map: Record<string, string[]> = {
    '金融 / 投资': ['金融', '分析师', '基金', '银行', '投资'],
    '科技 / 工程': ['工程师', '算法', '软件', '建筑师', '建筑', '土木'],
    '医疗 / 科研': ['医师', '医生', '研究员', '生物'],
    '法律': ['律师', '法律'],
    '设计 / 艺术': ['设计', '创意', '时装', '编辑'],
    '教育 / 咨询': ['咨询师', '心理'],
    '创业 / 管理': ['CEO', '创始人', '总监', '经理'],
    '贸易 / 商务': ['贸易', '外贸'],
  };
  return (map[filter] || []).some(kw => job.includes(kw));
};

export default function FeaturedMembers() {
  const [liked, setLiked] = useState<number[]>([]);
  const [videoModal, setVideoModal] = useState<number | null>(null);
  const navigate = useNavigate();
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();

  // Filter state
  const [gender, setGender] = useState<'all' | 'female' | 'male'>('all');
  const [ageMin, setAgeMin] = useState(20);
  const [ageMax, setAgeMax] = useState(40);
  const [location, setLocation] = useState('全部城市');
  const [profession, setProfession] = useState('全部职业');
  const [searchText, setSearchText] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Determine gender from member data (ladies have height ≤170 or weight ≤55 as heuristic, or check name)
  const femaleNames = ['雅婷', '晓雪', '美琳', '诗涵', '若曦', '嘉怡', '芷晴', '欣妍'];
  const isFemale = (name: string) => femaleNames.includes(name);

  const filtered = useMemo(() => {
    return featuredMembers.filter(m => {
      const memberIsFemale = isFemale(m.name);
      if (gender === 'female' && !memberIsFemale) return false;
      if (gender === 'male' && memberIsFemale) return false;
      if (m.age < ageMin || m.age > ageMax) return false;
      if (location !== '全部城市' && !m.location.includes(location)) return false;
      if (!professionMatch(m.job, profession)) return false;
      if (searchText.trim()) {
        const q = searchText.toLowerCase();
        if (
          !m.name.toLowerCase().includes(q) &&
          !m.job.toLowerCase().includes(q) &&
          !m.location.toLowerCase().includes(q) &&
          !m.education.toLowerCase().includes(q)
        ) return false;
      }
      return true;
    });
  }, [gender, ageMin, ageMax, location, profession, searchText]);

  const { ref: gridRef, visibleItems } = useStaggerAnimation(filtered.length);

  const toggleLike = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setLiked(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const openVideo = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setVideoModal(id);
  };

  const hasActiveFilters = gender !== 'all' || ageMin !== 20 || ageMax !== 40 || location !== '全部城市' || profession !== '全部职业';

  const resetFilters = () => {
    setGender('all');
    setAgeMin(20);
    setAgeMax(40);
    setLocation('全部城市');
    setProfession('全部职业');
    setSearchText('');
  };

  return (
    <section id="featured" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className="text-center mb-10 transition-all duration-700"
          style={{ opacity: headerVisible ? 1 : 0, transform: headerVisible ? 'translateY(0)' : 'translateY(30px)' }}
        >
          <div className="inline-flex items-center gap-2 border border-red-300 text-red-600 rounded-full px-4 py-1.5 text-xs font-semibold mb-4">
            <i className="ri-star-line"></i> 精选推荐 Featured Matches
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-3" style={{ fontFamily: "'Noto Serif SC', serif" }}>
            认证会员 等待与您相遇
          </h2>
          <p className="text-gray-400 text-sm max-w-lg mx-auto">
            所有资料均经过视频验证，学历证书已核实，确保真实可信
          </p>
        </div>

        {/* ── Search & Filter Bar ── */}
        <div className="mb-8 bg-gray-50 border border-gray-100 rounded-2xl p-4 md:p-5">
          {/* Top row: search + filter toggle */}
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            {/* Search input */}
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
              <input
                type="text"
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                placeholder="搜索姓名、职业、城市、学历..."
                className="w-full pl-9 pr-4 py-2.5 text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:border-red-400 transition-all"
              />
              {searchText && (
                <button
                  onClick={() => setSearchText('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-sm"></i>
                </button>
              )}
            </div>

            {/* Filter toggle button */}
            <button
              onClick={() => setShowFilters(v => !v)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-all cursor-pointer whitespace-nowrap border ${
                showFilters || hasActiveFilters
                  ? 'bg-red-600 text-white border-red-600'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-red-300'
              }`}
            >
              <i className="ri-equalizer-line"></i>
              筛选条件
              {hasActiveFilters && (
                <span className="w-5 h-5 flex items-center justify-center bg-white/30 rounded-full text-xs font-bold">
                  {[gender !== 'all', ageMin !== 20 || ageMax !== 40, location !== '全部城市', profession !== '全部职业'].filter(Boolean).length}
                </span>
              )}
            </button>

            {hasActiveFilters && (
              <button
                onClick={resetFilters}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm text-gray-500 hover:text-red-500 transition-all cursor-pointer whitespace-nowrap border border-gray-200 bg-white"
              >
                <i className="ri-refresh-line"></i>重置
              </button>
            )}
          </div>

          {/* Expanded filter panel */}
          {showFilters && (
            <div className="border-t border-gray-100 pt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Gender */}
              <div>
                <p className="text-xs font-semibold text-gray-500 mb-2">性别</p>
                <div className="flex gap-1 bg-white border border-gray-200 rounded-xl p-1">
                  {([
                    { key: 'all', label: '全部', icon: 'ri-team-line' },
                    { key: 'female', label: '女性', icon: 'ri-women-line' },
                    { key: 'male', label: '男性', icon: 'ri-men-line' },
                  ] as { key: 'all' | 'female' | 'male'; label: string; icon: string }[]).map(g => (
                    <button
                      key={g.key}
                      onClick={() => setGender(g.key)}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                        gender === g.key ? 'bg-red-600 text-white' : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <i className={`${g.icon} text-sm`}></i>
                      {g.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Age range */}
              <div>
                <p className="text-xs font-semibold text-gray-500 mb-2">
                  年龄范围
                  <span className="ml-2 text-red-500 font-bold">{ageMin} – {ageMax} 岁</span>
                </p>
                <div className="bg-white border border-gray-200 rounded-xl px-3 py-2.5 space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400 w-6">最小</span>
                    <input
                      type="range"
                      min={18}
                      max={ageMax - 1}
                      value={ageMin}
                      onChange={e => setAgeMin(Number(e.target.value))}
                      className="flex-1 accent-red-500 cursor-pointer"
                    />
                    <span className="text-xs font-semibold text-gray-700 w-6 text-right">{ageMin}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400 w-6">最大</span>
                    <input
                      type="range"
                      min={ageMin + 1}
                      max={50}
                      value={ageMax}
                      onChange={e => setAgeMax(Number(e.target.value))}
                      className="flex-1 accent-red-500 cursor-pointer"
                    />
                    <span className="text-xs font-semibold text-gray-700 w-6 text-right">{ageMax}</span>
                  </div>
                </div>
              </div>

              {/* Location */}
              <div>
                <p className="text-xs font-semibold text-gray-500 mb-2">所在城市</p>
                <div className="relative">
                  <i className="ri-map-pin-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                  <select
                    value={location}
                    onChange={e => setLocation(e.target.value)}
                    className="w-full pl-8 pr-4 py-2.5 text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:border-red-400 transition-all cursor-pointer appearance-none"
                  >
                    {LOCATIONS.map(l => (
                      <option key={l} value={l}>{l}</option>
                    ))}
                  </select>
                  <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none"></i>
                </div>
              </div>

              {/* Profession */}
              <div>
                <p className="text-xs font-semibold text-gray-500 mb-2">职业类别</p>
                <div className="relative">
                  <i className="ri-briefcase-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                  <select
                    value={profession}
                    onChange={e => setProfession(e.target.value)}
                    className="w-full pl-8 pr-4 py-2.5 text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:border-red-400 transition-all cursor-pointer appearance-none"
                  >
                    {PROFESSIONS.map(p => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                  <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none"></i>
                </div>
              </div>
            </div>
          )}

          {/* Quick filter chips */}
          {!showFilters && (
            <div className="flex flex-wrap gap-2">
              {/* Gender chips */}
              {(['all', 'female', 'male'] as const).map(g => (
                <button
                  key={g}
                  onClick={() => setGender(g)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap border ${
                    gender === g ? 'bg-red-600 text-white border-red-600' : 'bg-white text-gray-500 border-gray-200 hover:border-red-300'
                  }`}
                >
                  <i className={`${g === 'female' ? 'ri-women-line' : g === 'male' ? 'ri-men-line' : 'ri-team-line'} text-xs`}></i>
                  {g === 'all' ? '全部' : g === 'female' ? '女性' : '男性'}
                </button>
              ))}
              <div className="w-px bg-gray-200 mx-1 self-stretch"></div>
              {/* Age quick chips */}
              {[
                { label: '20–25岁', min: 20, max: 25 },
                { label: '26–30岁', min: 26, max: 30 },
                { label: '31–35岁', min: 31, max: 35 },
                { label: '36–40岁', min: 36, max: 40 },
              ].map(a => (
                <button
                  key={a.label}
                  onClick={() => { setAgeMin(a.min); setAgeMax(a.max); }}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap border ${
                    ageMin === a.min && ageMax === a.max ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-gray-500 border-gray-200 hover:border-amber-300'
                  }`}
                >
                  {a.label}
                </button>
              ))}
              <div className="w-px bg-gray-200 mx-1 self-stretch"></div>
              {/* Location quick chips */}
              {['上海', '香港', '新加坡', '伦敦', '悉尼'].map(l => (
                <button
                  key={l}
                  onClick={() => setLocation(location === l ? '全部城市' : l)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap border ${
                    location === l ? 'bg-rose-500 text-white border-rose-500' : 'bg-white text-gray-500 border-gray-200 hover:border-rose-300'
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Results count */}
        <div className="flex items-center justify-between mb-5">
          <p className="text-sm text-gray-500">
            共找到 <strong className="text-gray-800">{filtered.length}</strong> 位认证会员
            {hasActiveFilters && <span className="text-red-500 ml-1">（已筛选）</span>}
          </p>
          {filtered.length > 0 && (
            <p className="text-xs text-gray-400">按匹配度排序</p>
          )}
        </div>

        {/* Cards Grid */}
        {filtered.length === 0 ? (
          <div className="text-center py-20 text-gray-400">
            <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-4">
              <i className="ri-search-line text-2xl"></i>
            </div>
            <p className="text-base font-medium text-gray-500 mb-2">暂无符合条件的会员</p>
            <p className="text-sm text-gray-400 mb-5">试试调整筛选条件，发现更多缘分</p>
            <button
              onClick={resetFilters}
              className="px-6 py-2.5 bg-red-600 text-white rounded-full text-sm font-medium cursor-pointer hover:bg-red-700 transition-all whitespace-nowrap"
            >
              重置筛选条件
            </button>
          </div>
        ) : (
          <div ref={gridRef as React.RefObject<HTMLDivElement>} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((member, index) => (
              <div
                key={member.id}
                onClick={() => navigate(`/profile/${member.id}`)}
                className="bg-white border border-gray-100 rounded-2xl overflow-hidden hover:-translate-y-2 transition-all duration-500 cursor-pointer group"
                style={{
                  opacity: visibleItems[index] ? 1 : 0,
                  transform: visibleItems[index] ? 'translateY(0)' : 'translateY(40px)',
                  transitionDelay: `${index * 60}ms`,
                }}
              >
                {/* Photo */}
                <div className="relative">
                  <div className="w-full h-56 overflow-hidden">
                    <img
                      src={member.avatar}
                      alt={member.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  {member.videoVerified && (
                    <div className="absolute top-3 left-3 flex items-center gap-1 bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                      <i className="ri-video-line text-xs"></i> 视频认证
                    </div>
                  )}
                  <div className="absolute top-3 right-3 bg-yellow-400 text-yellow-900 text-xs px-2 py-1 rounded-full font-semibold">
                    {member.education.split('·')[0].trim()}
                  </div>
                  <div className="absolute bottom-3 right-3 w-12 h-12 flex items-center justify-center bg-white rounded-full">
                    <div className="text-center">
                      <div className="text-red-600 font-bold text-sm leading-none">{member.match}%</div>
                      <div className="text-gray-400 text-[9px]">匹配</div>
                    </div>
                  </div>
                </div>

                {/* Info */}
                <div className="p-4">
                  <div className="flex items-start justify-between mb-2.5">
                    <div>
                      <h3 className="text-lg font-bold text-gray-800">{member.name}，{member.age}岁</h3>
                      <div className="flex items-center gap-1.5 mt-1">
                        <span className="text-base leading-none">{(member as { flag?: string }).flag || '🇨🇳'}</span>
                        <span className="text-xs font-semibold text-gray-700">{member.location}</span>
                        <span className="text-gray-300 text-xs">·</span>
                        <span className="text-xs text-gray-400">{(member as { locationEn?: string }).locationEn || ''}</span>
                      </div>
                    </div>
                    {member.verified && (
                      <div className="w-7 h-7 flex items-center justify-center bg-green-50 rounded-full">
                        <i className="ri-shield-check-fill text-green-500 text-sm"></i>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-1.5 text-xs mb-2 bg-amber-50 rounded-lg px-2.5 py-1.5">
                    <i className="ri-graduation-cap-line text-amber-500"></i>
                    <span className="font-semibold text-amber-700">{member.education}</span>
                    <span className="text-amber-300">·</span>
                    <span className="text-amber-600">{member.school}</span>
                  </div>

                  <div className="flex items-center gap-1.5 text-xs mb-3 text-gray-500">
                    <i className="ri-briefcase-line text-gray-400"></i>
                    <span className="font-medium text-gray-600">{member.job}</span>
                    <span className="text-gray-300">·</span>
                    <span>{member.income}</span>
                  </div>

                  <p className="text-xs text-gray-400 mb-4 line-clamp-2 leading-relaxed">{member.bio}</p>

                  <div className="flex gap-1.5 mb-4 flex-wrap">
                    {member.videoVerified && (
                      <span className="flex items-center gap-1 bg-green-50 text-green-600 text-xs px-2 py-0.5 rounded-full border border-green-100">
                        <i className="ri-video-line text-xs"></i> 视频认证
                      </span>
                    )}
                    {member.eduVerified && (
                      <span className="flex items-center gap-1 bg-amber-50 text-amber-600 text-xs px-2 py-0.5 rounded-full border border-amber-100">
                        <i className="ri-graduation-cap-line text-xs"></i> 学历认证
                      </span>
                    )}
                    {member.workVerified && (
                      <span className="flex items-center gap-1 bg-red-50 text-red-500 text-xs px-2 py-0.5 rounded-full border border-red-100">
                        <i className="ri-briefcase-line text-xs"></i> 工作认证
                      </span>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={(e) => openVideo(member.id, e)}
                      className="flex-1 flex items-center justify-center gap-1.5 bg-red-600 text-white py-2 rounded-full text-sm font-medium hover:bg-red-700 transition-all cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-video-chat-line"></i> 视频聊天
                    </button>
                    <button
                      onClick={(e) => toggleLike(member.id, e)}
                      className={`w-10 h-9 flex items-center justify-center rounded-full border transition-all cursor-pointer ${liked.includes(member.id) ? 'bg-red-50 border-red-300 text-red-500' : 'border-gray-200 text-gray-400 hover:border-red-300 hover:text-red-400'}`}
                    >
                      <i className={liked.includes(member.id) ? 'ri-heart-fill' : 'ri-heart-line'}></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="text-center mt-10 flex flex-col sm:flex-row gap-4 justify-center">
          <button className="px-8 py-3 border-2 border-red-600 text-red-600 rounded-full font-semibold text-sm hover:bg-red-50 transition-all cursor-pointer whitespace-nowrap">
            查看更多会员 <i className="ri-arrow-right-line ml-1"></i>
          </button>
          <button
            onClick={() => navigate('/stories')}
            className="px-8 py-3 bg-red-600 text-white rounded-full font-semibold text-sm hover:bg-red-700 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-heart-2-line mr-1"></i> 查看成功故事
          </button>
        </div>
      </div>

      {/* Video Chat Modal */}
      {videoModal !== null && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setVideoModal(null)}>
          <div className="bg-white rounded-2xl p-8 max-w-sm w-full text-center" onClick={e => e.stopPropagation()}>
            <div className="w-16 h-16 flex items-center justify-center bg-red-100 rounded-full mx-auto mb-4">
              <i className="ri-video-chat-line text-red-500 text-2xl"></i>
            </div>
            <h3 className="text-lg font-bold text-gray-800 mb-2">发起视频通话</h3>
            <p className="text-gray-500 text-sm mb-6">视频通话是我们验证真实身份的核心功能，请登录后使用。</p>
            <div className="flex gap-3">
              <button onClick={() => setVideoModal(null)} className="flex-1 py-2.5 border border-gray-200 rounded-full text-sm text-gray-600 cursor-pointer whitespace-nowrap">取消</button>
              <button className="flex-1 py-2.5 bg-red-600 text-white rounded-full text-sm font-medium cursor-pointer whitespace-nowrap">立即登录</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
